import React, { Suspense, useEffect, useMemo, useState } from 'react';

import { SidebarNavigation } from '@flipdish/ui-library/components/organisms/SidebarNavigation';
import { type Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import FlipdishAppBanner from 'components/FlipdishAppBanner/FlipdishAppBanner';
import { connect } from 'react-redux';
import { checkVisibility } from 'react-redux-permissions/dist/core';
import {
  matchPath,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from 'react-router';

import { appsActions } from '../../actions/apps.actions';
import FlipdishAgentAssistant from '../../components/FlipdishAgent/FlipdishAgentAssistant';
import { getOrgByBrandId } from '../../components/RMS/organisation.services';
import { setCurrentOrg } from '../../components/RMS/rms.actions';
import { generalConstants } from '../../constants/general.constants';
import { useIsWebView } from '../../custom-hooks/useIsWebView';
import useTailwindPortal from '../../custom-hooks/useTailwindPortal';
import { lazyWithRetry } from '../../helpers/utilities';
import { portalRoutes } from '../../routes/portal.routes';
import { flagService } from '../../services';
import ScrollToTop from './ScrollToTop';
import SideNav from './SideNav';
import TopNav from './TopNav';
import { useSidebarNavItems } from './useSidebarNavItems';

const isDev = !window.location?.host?.includes('portal.flipdish.com');
const FlipdishToolbar = lazyWithRetry(
  () => import('../../components/FlipdishToolbar/FlipdishToolbar')
);

const { SCROLLABLE_CONTAINER } = generalConstants;

const tutorialDrawerWidth = 408;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  rootNew: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  wrapper: {
    flexGrow: 1,
    width: '100%',
    display: 'flex',
    overflow: 'hidden',
  },
  // SidebarNavigation is `position: fixed` with a hardcoded Tailwind `z-50`. The old SideNav was a
  // MUI Drawer pinned to `position: static`, so it never took part in stacking and content overlays
  // always painted above it. Now that the nav stacks, every ad-hoc z-index in the app competes with
  // it in the root stacking context: filters at 99 paint over the nav flyout (COG-759), react-select
  // menus at 10 hide behind it. Giving the nav its own stacking context makes the library's `z-50`
  // private and leaves one value to reason about.
  //
  // 200 sits above the app's ad-hoc content values (9, 10, 32, 33, 99, 100, 101) and below MUI's
  // `theme.zIndex.modal` (1300), so dialogs, menus and tooltips portalled to <body> stay above the
  // nav. That ceiling only exists while MUI is still here — when it goes, this value moves, which is
  // why it lives in one place.
  //
  // REMOVE (this rule's zIndex and the `@global` rule below) when all of Portal is Tailwind and
  // MUI is gone: the ad-hoc z-indexes competing with the nav go with it, and the library's own
  // `z-50` works as designed.
  sidebarNavLayer: {
    flexShrink: 0,
    position: 'relative',
    zIndex: 200,
    '@media (min-width: 1024px)': {
      '& > .invisible': { display: 'block !important' },
    },
  },
  // Same 200 as above, for the mobile drawer: it portals to <body>, so it escapes
  // `sidebarNavLayer` and keeps its `z-50` in the root stacking context.
  //
  // Global rather than a prop because SidebarNavigation gives us no handle on the drawer: no
  // `className`/`style`, no slot override, `MobileNavigationDrawer` isn't re-exported so we can't
  // render it ourselves, and `data-testid` lands on the drawer's close button rather than the
  // overlay. Styling it from outside is the only route until the library exposes one — so
  // `lg:hidden` narrows the match to the drawer, since ui-library's Modal and CommandMenu share
  // `fixed inset-0 z-50` too.
  //
  // REMOVE (this rule's zIndex and the `@global` rule below) when all of Portal is Tailwind and
  // MUI is gone: the ad-hoc z-indexes competing with the nav go with it, and the library's own
  // `z-50` works as designed.
  '@global': {
    'body > [data-tailwind].fixed.inset-0.lg\\:hidden': {
      zIndex: 200,
    },
  },
  rightSide: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
  },
  content: {
    flexGrow: 1,
    width: '100%',
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    overflowX: 'auto',
  },
  switch: {
    flexGrow: 1,
    display: 'flex',
    overflowY: 'auto',
    overflowX: 'hidden',
    flexDirection: 'column',
  },
  tutorialDrawerOpen: {
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.up('sm')]: {
      marginRight: tutorialDrawerWidth,
    },
  },
  tutorialDrawerClose: {
    marginRight: 0,
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
}));

const createRoute = (
  props: PortalRoutePropsExtended | PortalRedirectPropsExtended,
  appPermissions: AppState['permissions']
) => {
  if ('to' in props) {
    // only Redirect has `to` prop required
    const { name, key = '', ...rest } = props;
    return <Redirect {...rest} key={`${name}.${key}`} />;
  }

  if ('permissions' in props) {
    const { permissions, name, key = '', ...rest } = props;
    if (checkVisibility(appPermissions, permissions, [])) {
      return <Route {...rest} key={`${name}.${key}`} />;
    }
    return null;
  }

  const { name, key = '', ...rest } = props;
  return <Route {...rest} key={`${name}.${key}`} />;
};

type Props = MappedDispatch & MappedState;

const Portal = (props: Props) => {
  const {
    currentApp,
    currentOrg,
    disablePropertyLevelAddOns,
    disableLegacyTeammate,
    foodSafetyAddOnPermissions,
    getApps,
    isHelpDrawerOpen,
    permissions,
    showNewSidebarNavigation,
    dispatchSetCurrentOrg,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const { appId } = useParams<{ appId: string }>();
  const isWebView = useIsWebView();
  const isOnSetupRoute = Boolean(matchPath(location.pathname, { path: '/:appId/setup' }));

  const { data: orgData } = useQuery({
    queryKey: [getOrgByBrandId, appId],
    queryFn: () => getOrgByBrandId(appId || ''),
    enabled: !!appId,
    retry: 1,
  });

  useEffect(() => {
    const org = orgData?.data?.data?.[0];
    if (org && org.orgId !== currentOrg?.orgId) {
      dispatchSetCurrentOrg(org);
    }
  }, [orgData, currentOrg]);

  useEffect(() => {
    if (!isWebView) return;
    return history.listen((location, action) => {
      if (action === 'REPLACE') return;
      const params = new URLSearchParams(location.search);
      const alreadySet = [...params.entries()].some(
        ([k, v]) => k.toLowerCase() === 'iswebview' && v.toLowerCase() === 'true'
      );
      if (!alreadySet) {
        params.set('isWebView', 'true');
        history.replace({ ...location, search: `?${params.toString()}` });
      }
    });
  }, [isWebView]);

  // Redirect to onboarding if conditions are met
  //useRedirectToOnboarding();

  const classes = useStyles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  // Old sidebar state
  const [open, setOpen] = useState(false);
  const [openSubDrawer, setOpenSubDrawer] = useState(false);

  // New sidebar state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    items: navItems,
    footerItems: navFooterItems,
    account: navAccount,
    activeUrl,
  } = useSidebarNavItems();
  useTailwindPortal();

  useEffect(() => {
    getApps();
  }, []);

  useEffect(() => {
    if (!showNewSidebarNavigation) {
      setOpen(matches);
    }
  }, [matches, showNewSidebarNavigation]);

  const toggleSideNav = () => {
    if (isMobile) {
      setOpen((prevOpen) => !prevOpen);
    } else if (!openSubDrawer) {
      setOpen((prevOpen) => !prevOpen);
    }
  };

  const drawerOpen = isHelpDrawerOpen === true;
  const drawerClosed = isHelpDrawerOpen === false;

  const routes = useMemo(() => {
    if (!currentApp.AppId) {
      return null;
    }
    return portalRoutes(currentApp.AppId, disablePropertyLevelAddOns, foodSafetyAddOnPermissions)
      .filter((r) => !(disableLegacyTeammate && 'name' in r && r.name === 'teammates'))
      .map((r) => createRoute(r, permissions));
  }, [currentApp.AppId, permissions, disableLegacyTeammate]);

  if (showNewSidebarNavigation) {
    return (
      <div className={classes.rootNew}>
        {!isWebView && (
          <div data-tailwind className={classes.sidebarNavLayer}>
            <SidebarNavigation
              activeUrl={activeUrl}
              items={navItems}
              footerItems={navFooterItems}
              account={navAccount}
              showMobileHeader={false}
              mobileMenuOpen={mobileMenuOpen}
              onMobileMenuOpenChange={setMobileMenuOpen}
            />
          </div>
        )}
        <div className={classes.rightSide}>
          {!isWebView && (
            <TopNav mobileMenuOpen={mobileMenuOpen} onMobileMenuOpenChange={setMobileMenuOpen} />
          )}
          <main className={classes.content}>
            <div
              className={clsx({
                [classes.switch]: true,
                [classes.tutorialDrawerOpen]: drawerOpen,
                [classes.tutorialDrawerClose]: drawerClosed,
              })}
              id={SCROLLABLE_CONTAINER}
            >
              {!isOnSetupRoute && <FlipdishAppBanner />}
              <ScrollToTop>
                <Switch>{routes}</Switch>
              </ScrollToTop>
            </div>
          </main>
        </div>
        {isDev && (
          <Suspense fallback={null}>
            <FlipdishToolbar />
          </Suspense>
        )}
        {!isWebView && <FlipdishAgentAssistant />}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {!isWebView && <TopNav open={open} toggleSideNav={toggleSideNav} />}
      <div className={classes.wrapper}>
        {!isWebView && (
          <SideNav
            open={open}
            toggleSideNav={toggleSideNav}
            openSubDrawer={openSubDrawer}
            setOpenSubDrawer={setOpenSubDrawer}
          />
        )}
        <main className={classes.content}>
          <div
            className={clsx({
              [classes.switch]: true,
              [classes.tutorialDrawerOpen]: drawerOpen,
              [classes.tutorialDrawerClose]: drawerClosed,
            })}
            id={SCROLLABLE_CONTAINER}
          >
            {!isOnSetupRoute && <FlipdishAppBanner />}
            <ScrollToTop>
              <Switch>{routes}</Switch>
            </ScrollToTop>
          </div>
        </main>
      </div>
      {isDev && (
        <Suspense fallback={null}>
          <FlipdishToolbar />
        </Suspense>
      )}
      {!isWebView && <FlipdishAgentAssistant />}
    </div>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
function mapStateToProps(state: AppState) {
  return {
    currentApp: state.currentApp,
    currentOrg: state.rms.currentOrg,
    isHelpDrawerOpen: state.drawer.helpDrawerOpen,
    permissions: state.permissions,
    disablePropertyLevelAddOns: flagService.isFlagOn(state, 'disablePropertyManagerLevelAddOns'),
    disableLegacyTeammate: flagService.isFlagOn(state, 'disableLegacyTeammate'),
    foodSafetyAddOnPermissions: flagService.isFlagOn(state, 'foodSafetyAddOnPermissions'),
    showNewSidebarNavigation: flagService.isFlagOn(state, 'showNewSidebarNavigation'),
  };
}

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
function mapDispatchToProps(dispatch: ThunkDispatch) {
  return {
    getApps: () => dispatch(appsActions.getApps(undefined, false, undefined, 1, 20)),
    dispatchSetCurrentOrg: (org: Parameters<typeof setCurrentOrg>[0]) =>
      dispatch(setCurrentOrg(org)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Portal);
